import {type ReactNode, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState} from 'react'

import {cellHasUnloadedItems} from '../cells/cell-has-unloaded-items'
import {composeCellKey} from '../provider/cell-key'
import {useBoardConfig, useBoardDataContext} from '../provider/contexts'
import type {CellAddress, ItemId} from '../types'
import {
  BoardFocusGridContext,
  type BoardFocusStore,
  BoardFocusStoreContext,
  BoardPositionRenderedContext,
} from './contexts'
import {boardFocusReducer, initialBoardFocusState} from './focus-reducer'
import type {BoardFocusGrid} from './types'

const NO_ITEM_IDS: readonly ItemId[] = []

/**
 * Holds the board's focus position and publishes it.
 *
 * Rendered inside the data and config contexts, because the board the reducer moves over
 * is built from both. Renders no DOM, like everything else in the provider.
 */
export function BoardFocusProvider({children}: {children: ReactNode}): ReactNode {
  const {data, cellsByAddress} = useBoardDataContext()
  const {getItemId} = useBoardConfig()

  // The two axes are the caller's stated facts, so they change only when the caller's
  // columns or rows do. Kept apart from the cell lookup below so that a caller passing an
  // inline `getItemId`, which is the usual way to call this board, does not rebuild them
  // on every render.
  const columnKeys = useMemo(() => data.columns.map(column => column.key), [data.columns])
  const rowKeys = useMemo(() => data.rows.map(row => row.key), [data.rows])

  /*
    Whether each axis draws the ghost that says it has more, which is exactly the condition
    the axis's paging component mounts one on: it has more, or a page is in flight, and it
    is not truncated. Truncation is the other half of an axis's statement and never appears
    beside having more, so an axis that has given up draws a notice rather than a ghost, and
    a notice is not somewhere focus goes.

    Read from what the caller stated, never from `columns.length`.
  */
  const hasColumnPaging = !(data.columnsTruncated ?? false) && (data.hasMoreColumns || (data.isLoadingColumns ?? false))
  const hasRowPaging = !(data.rowsTruncated ?? false) && (data.hasMoreRows || (data.isLoadingRows ?? false))

  const grid = useMemo<BoardFocusGrid>(
    () => ({
      columnKeys,
      rowKeys,
      itemIdsAt: (address: CellAddress) =>
        cellsByAddress.get(composeCellKey(address))?.items.map(item => getItemId(item)) ?? NO_ITEM_IDS,
      // The same condition `BoardItemPaging` draws on. A pair the caller never supplied has
      // nothing more by definition, so it has no ghost either.
      hasItemPagingAt: (address: CellAddress) => {
        const cell = cellsByAddress.get(composeCellKey(address))

        return cell !== undefined && (cellHasUnloadedItems(cell) || (cell.isLoadingItems ?? false))
      },
      hasColumnPaging,
      hasRowPaging,
    }),
    [columnKeys, rowKeys, cellsByAddress, getItemId, hasColumnPaging, hasRowPaging],
  )

  const [state, dispatch] = useReducer(boardFocusReducer, grid, initialBoardFocusState)

  // Repairs the position when the board moves underneath it: a column that paged out, a
  // card somebody else closed, a first page arriving into a board that had no cells at
  // all. It never moves focus, and it returns the state untouched when nothing moved, so
  // the usual case costs a dispatch and no render.
  useEffect(() => {
    dispatch({type: 'reconcile', grid})
  }, [grid])

  const claimCountRef = useRef(0)
  /*
    Whether anything on the board is drawing the position, which is what decides whether
    the root is a tab stop of its own.

    Seeded from whether there is a position at all rather than from a claim, because a
    claim is an effect and the server runs none: starting at `false` would put
    `tabIndex: 0` on the root and on the seeded card at once, so the markup a server sends
    has two ways into a widget that has exactly one, and the first client render flips a
    `tabindex` it already sent. The seeded position is the first card of the first cell,
    which every window draws, so the optimistic answer is the right one for a board that
    has just rendered.
  */
  const [isPositionRendered, setIsPositionRendered] = useState(() => state.position !== null)

  // Corrected once the items have had their say. Their effects run before this one, so a
  // claim count of zero here means nothing drew the position, and the root takes the stop
  // back rather than the board falling out of the tab order.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state -- seeding `false` instead is the bug above: whether an element drew the position is only knowable after that element's effect, and a render cannot ask.
    if (claimCountRef.current === 0) setIsPositionRendered(false)
  }, [])

  // The reducer's state, mirrored for the components that subscribe to one fact about it
  // rather than to the whole of it.
  const stateRef = useRef(state)
  const listenersRef = useRef<Set<() => void>>(new Set())

  // The focus event an element at a position last said it took. A ref rather than state,
  // because it is read inside the handling of the same event that set it and nothing on
  // the board draws differently for it.
  const focusAtPositionRef = useRef<Event | null>(null)

  // Every subscriber removes itself, so this is only for a board torn down while some of
  // them are still mounted, which is the ordinary case on unmount.
  useEffect(() => {
    const listeners = listenersRef.current

    return () => {
      listeners.clear()
    }
  }, [])

  // A layout effect, so a subscriber re-renders and the roving item takes focus in the
  // same commit the move happened in, before anything is painted.
  useLayoutEffect(() => {
    stateRef.current = state
    for (const listener of listenersRef.current) listener()
  }, [state])

  const store = useMemo<BoardFocusStore>(
    () => ({
      dispatch,
      getState: () => stateRef.current,
      subscribe: (listener: () => void) => {
        listenersRef.current.add(listener)

        return () => {
          listenersRef.current.delete(listener)
        }
      },
      claimPosition: () => {
        claimCountRef.current += 1
        setIsPositionRendered(true)

        return () => {
          claimCountRef.current -= 1
          if (claimCountRef.current === 0) {
            setIsPositionRendered(false)
          }
        }
      },
      reportFocusAtPosition: (focusEvent: Event) => {
        focusAtPositionRef.current = focusEvent
      },
      isFocusAtPosition: (focusEvent: Event) => focusAtPositionRef.current === focusEvent,
    }),
    [],
  )

  return (
    <BoardFocusStoreContext value={store}>
      <BoardFocusGridContext value={grid}>
        <BoardPositionRenderedContext value={isPositionRendered}>{children}</BoardPositionRenderedContext>
      </BoardFocusGridContext>
    </BoardFocusStoreContext>
  )
}
